// @flow
import React, { type Element } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { pure } from 'recompose';
import classNames from 'classnames';
import { Track, analytics, urlCatalog } from 'angieslist-app-platform';
import { find, get } from 'lodash';
import menuAnalytics from './menuAnalytics';
import ProfileIndicator from './ProfileIndicator';
import profileSections from '../../common/profileSections';
import type { Entitlement } from '../../../../app/types';
import './Menu.scss';

export type Props = {
  serviceProviderId: string,
  isAdvertiser: boolean,
  currentPage: string,
  mobileUser: boolean,
  experiment: any,
  entitlements: Entitlement[],
  accountingSyncUrl: string,
};

const getDealsUrl = serviceProviderId => `${urlCatalog.officeRoot}/app/store/${serviceProviderId}/shop`;

export const MenuComponent = ({
  serviceProviderId, isAdvertiser, currentPage, mobileUser, experiment, entitlements,
  accountingSyncUrl,
}: Props): Element<"div"> => {
  const hasBmt = find(entitlements, e => e.permission === 'BusinessManagementTools' && e.scopeId === String(serviceProviderId)) || false;
  const enableBmtQuickBooksSync = get(experiment, 'experiments.enableBmtQuickBooksSync.enabled', false);
  const isAdmin = find(entitlements, e => e.permission === 'admin' && e.scopeId === String(serviceProviderId) && e.scope === 'ServiceProvider');
  const showQbo = enableBmtQuickBooksSync && isAdmin && hasBmt;

  return (
    <div className="Edit-Profile--Menu-Component">
      <ul id="Edit-Profile--Menu-List" >
        <li>
          <div className="editProfileMenuListItemStyle" id="Contact-Information-div-parent">
            <div className="editProfileMenuListLinkItemStyle" >
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-Contact-Information"
                  to={`/app/profile/${serviceProviderId}/contact-information`}
                  activeClassName="active"
                >
                  <span className="nav-item">Contact</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.CONTACT} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-About"
                  to={`/app/profile/${serviceProviderId}/about`}
                  activeClassName="active"
                  className={classNames({ active: !currentPage && !mobileUser })}
                >
                  <span className="nav-item">About</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.ABOUT} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-Hours"
                  to={`/app/profile/${serviceProviderId}/hours`}
                  activeClassName="active"
                >
                  <span className="nav-item">Hours</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.HOURS} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-Service-Details"
                  to={`/app/profile/${serviceProviderId}/service-details`}
                  activeClassName="active"
                >
                  <span className="nav-item">Service details</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.SERVICE_DETAILS} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-Project-Showcase"
                  to={urlCatalog.office.spProfile.showcases(serviceProviderId)}
                  activeClassName="active"
                >
                  <span className="nav-item">Project showcase</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.SHOWCASE} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <a
                  id="Edit-Profile--MenuNav-Photo-Gallery"
                  href={urlCatalog.office.spProfile.images(serviceProviderId)}
                >
                  <span className="nav-item">Photos </span>
                </a>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.PHOTOS} />
          </div>
        </li>
        <li>
          <div className="editProfileMenuListItemStyle">
            <div className="editProfileMenuListLinkItemStyle">
              <Track>
                <Link
                  id="Edit-Profile--MenuNav-Licensing"
                  to={`/app/profile/${serviceProviderId}/licensing`}
                  activeClassName="active"
                >
                  <span className="nav-item">Licensing & insurance</span>
                </Link>
              </Track>
            </div>
            <ProfileIndicator sectionName={profileSections.LICENSING} />
          </div>
        </li>
        <If condition={isAdvertiser}>
          <li>
            <a
              id="Edit-Profile--MenuNav-Advertising-Payment-Info"
              href={urlCatalog.office.paymentMethod(serviceProviderId)}
            >
              <span className="nav-item">Advertising payment info </span>
            </a>
          </li>
        </If>
        <li>
          <a
            id="Edit-Profile--MenuNav-Deals"
            href={getDealsUrl(serviceProviderId)}
          >
            <span className="nav-item">Deals </span>
          </a>
        </li>
      </ul>
      <If condition={hasBmt}>
        <strong className="nav-item">Manage my business</strong>
        <ul id="Edit-Profile--Manage-Menu-List">
          <li>
            <a
              id="Edit-Profile--ManageMenuNav-BMT-Customers"
              href={urlCatalog.office.bmt.customers(serviceProviderId)}
            >
              <span className="nav-item">Customer list</span>
            </a>
          </li>

          <li>
            <a
              id="Edit-Profile--ManageMenuNav-BMT-PriceList"
              href={urlCatalog.office.bmt.priceList(serviceProviderId)}
            >
              <span className="nav-item">Price list</span>
            </a>
          </li>
          <If condition={showQbo}>
            <li>
              <Track>
                <a
                  id="Edit-Profile--ManageMenuNav-BMT-QBO"
                  href={`${accountingSyncUrl}&serviceProviderId=${serviceProviderId}`}
                  target="_blank"
                >
                  <span className="nav-item">QuickBooks Online Sync</span>
                </a>
              </Track>
            </li>
          </If>
        </ul>
      </If>
    </div>
  );
};

export default (compose(
  analytics({ ruleset: menuAnalytics }),
  connect(state => ({
    serviceProviderId: state.serviceProviderId,
    serviceProviderProfile: state.serviceProviderProfile,
  })),
  pure,
)(MenuComponent): any);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/Menu.js